import { Bot, Code2, Plug, RefreshCw } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useI18n } from "../i18n";
import { SectionHeading } from "./ui/card";
import { Reveal } from "./reveal";

/* Mini-terminal ilustrativo do card "Software sob medida" */
function TerminalMock() {
  const linhas = [
    { p: "$", t: "vorto build --projeto portal-cliente", c: "text-paper-on-ink/90" },
    { p: ">", t: "integrando IA · conectando ERP · 3 telas", c: "text-paper-on-ink/55" },
    { p: ">", t: "testes e2e · 42 ok · 0 falhas", c: "text-paper-on-ink/55" },
    { p: "✓", t: "no ar em 3 semanas", c: "text-[#60A5FA]" },
  ];
  return (
    <div className="overflow-hidden rounded-lg border border-ink-2 bg-ink font-mono text-[11px] leading-relaxed shadow-[0_16px_40px_-20px_rgb(26_25_21/0.5)] sm:text-xs">
      <div className="flex items-center gap-1.5 border-b border-white/[0.08] px-3.5 py-2.5">
        <span className="size-2.5 rounded-full bg-white/15" />
        <span className="size-2.5 rounded-full bg-white/15" />
        <span className="size-2.5 rounded-full bg-white/15" />
      </div>
      <div className="flex flex-col gap-1 px-3.5 py-3 text-left">
        {linhas.map((l, i) => (
          <div key={i} className="flex gap-2">
            <span className={l.p === "✓" ? "text-[#60A5FA]" : "text-amber-mark"}>{l.p}</span>
            <span className={l.c}>{l.t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Bolhas de conversa do card "Agentes de IA" */
function ChatMock() {
  return (
    <div className="flex flex-col gap-2 text-left">
      <div className="max-w-[85%] self-start rounded-xl rounded-bl-sm border border-line bg-surface px-3.5 py-2.5 text-xs leading-relaxed text-foreground/85 shadow-[0_1px_2px_rgb(26_25_21/0.05)]">
        Preciso do código do filtro do CX-9 2020
      </div>
      <div className="max-w-[92%] self-end rounded-xl rounded-br-sm bg-accent px-3.5 py-2.5 text-xs leading-relaxed text-white shadow-[0_6px_16px_-8px_rgb(29_78_216/0.55)]">
        Filtro de óleo FK-083 · part 15K0-14-302 · em estoque: 12un · prateleira B-14.
        <span className="mt-1 block text-[10px] text-white/65">fonte: catálogo oficial · 0,8s</span>
      </div>
    </div>
  );
}

/* Chips de integração do card "Integração com seus sistemas" */
function IntegrationsMock() {
  const sistemas = ["ERP", "Banco de dados", "WhatsApp", "Planilhas", "APIs"];
  return (
    <div className="flex flex-wrap gap-2">
      {sistemas.map((s, i) => (
        <span
          key={s}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-3 py-1.5 text-xs text-muted-foreground shadow-[0_1px_2px_rgb(26_25_21/0.04)]"
        >
          <RefreshCw
            className="size-3 animate-spin text-accent [animation-duration:7s]"
            style={{ animationDelay: `${i * 0.7}s` }}
          />
          {s}
        </span>
      ))}
    </div>
  );
}

function ServiceRow({
  n,
  icon: Icon,
  titulo,
  desc,
  bullets,
  mock,
  invert = false,
  delay = 0,
}: {
  n: string;
  icon: LucideIcon;
  titulo: string;
  desc: string;
  bullets: string[];
  mock: ReactNode;
  invert?: boolean;
  delay?: number;
}) {
  return (
    <Reveal delay={delay}>
      <article className="grid items-center gap-8 border-t border-line py-12 sm:py-14 lg:grid-cols-2 lg:gap-16">
        <div className={invert ? "lg:order-2" : undefined}>
          <div className="flex items-baseline gap-4">
            <span className="font-display text-3xl font-medium text-subtle/60 tabular-nums sm:text-4xl">
              {n}
            </span>
            <span className="grid size-9 translate-y-1.5 place-items-center rounded-lg bg-accent-soft text-accent">
              <Icon className="size-[18px]" strokeWidth={1.8} />
            </span>
          </div>
          <h3 className="mt-4 font-display text-2xl font-medium tracking-tight sm:text-[1.7rem]">
            {titulo}
          </h3>
          <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-muted-foreground">{desc}</p>
          <ul className="mt-5 flex flex-col gap-2">
            {bullets.map((li) => (
              <li key={li} className="flex items-center gap-2.5 text-sm text-foreground/80">
                <span className="size-1 rounded-full bg-accent" />
                {li}
              </li>
            ))}
          </ul>
        </div>
        <div className={invert ? "lg:order-1" : undefined}>{mock}</div>
      </article>
    </Reveal>
  );
}

/** As três frentes da Vorto — índice editorial, uma linha por frente. */
export function Services() {
  const { t } = useI18n();

  const lista = (n: number) => [1, 2, 3].map((i) => t(`serv.c${n}.li${i}`));

  return (
    <section id="servicos" className="relative scroll-mt-24 bg-surface-2/50 py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <SectionHeading
            index="02"
            label={t("serv.rotulo")}
            title={t("serv.titulo")}
            description={t("serv.desc")}
            className="mb-12 sm:mb-16"
          />
        </Reveal>

        <ServiceRow
          n="01"
          icon={Code2}
          titulo={t("serv.c1.titulo")}
          desc={t("serv.c1.desc")}
          bullets={lista(1)}
          mock={<TerminalMock />}
        />
        <ServiceRow
          n="02"
          icon={Bot}
          titulo={t("serv.c2.titulo")}
          desc={t("serv.c2.desc")}
          bullets={lista(2)}
          mock={<ChatMock />}
          invert
          delay={0.05}
        />
        <ServiceRow
          n="03"
          icon={Plug}
          titulo={t("serv.c3.titulo")}
          desc={t("serv.c3.desc")}
          bullets={lista(3)}
          mock={<IntegrationsMock />}
          delay={0.05}
        />
      </div>
    </section>
  );
}
